// EconomicCalendar — Horizontal-Scroll-Cards für Wirtschaftsnews.
// Design-Vorlage: 21st.dev/r/ravikatiyar162/economic-calendar (Doc-Approval
// 2026-05-17, Skill upro-redesign aktiv). Übersetzt + auf unsere
// Tailwind-v4 / glass + amber Palette gehoben.
//
// Pro Tag eine Card-Row: Header oben (Datum + Pfeile), horizontal-scrollbare
// Cards mit Time-Badge · Volatility-Bars · Flag · Indicator · 3-Spalten-Footer
// (Aktuell · Prognose · Vorher) + „Frag EVE"-Button.

import { motion, AnimatePresence } from 'framer-motion';
import { CalendarPlus, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getIndicatorExplanation } from '@/lib/macro-glossary';
import {
  buildGoogleCalendarUrl,
  downloadSingleEventIcal,
  type SingleCalEvent,
} from '@/lib/macro-ical-client';

export type Impact = 'high' | 'medium' | 'low';

export interface EconomicEvent {
  /** ISO-2 Country-Code (US · DE · EU · GB). */
  countryCode: string;
  /** ISO-Datum YYYY-MM-DD (für Kalender-Export). */
  date: string;
  /** Berlin-Zeit „HH:MM" (z.B. „14:30"). */
  timeBerlin: string;
  /** ET-Zeit „HH:MM" (z.B. „08:30"). */
  timeEt: string;
  /** Indicator-Name z.B. „CPI YoY". */
  eventName: string;
  /** Werte als formatierte Strings ("—" wenn null). */
  actual: string | null;
  forecast: string | null;
  prior: string | null;
  impact: Impact;
}

interface EconomicCalendarProps {
  /** Wochentag + Datum als Header z.B. „Montag · 19.05.2026". */
  title: string;
  /** „Heute"-Badge wenn das die aktuelle Tagessektion ist. */
  isToday?: boolean;
  events: ReadonlyArray<EconomicEvent>;
  /** Klick auf Card-Footer-Button. */
  onAskEve?: (event: EconomicEvent) => void;
}

const IMPACT_BAR_COUNT: Record<Impact, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

const IMPACT_BAR_TONE: Record<Impact, string> = {
  high: 'bg-rose-400',
  medium: 'bg-amber-400',
  low: 'bg-neutral-500',
};

function VolatilityBars({ impact }: { impact: Impact }) {
  const filled = IMPACT_BAR_COUNT[impact];
  const tone = IMPACT_BAR_TONE[impact];
  return (
    <div className="flex items-end gap-0.5" title={`Impact: ${impact}`}>
      {[2, 3, 4].map((h, i) => (
        <span
          key={i}
          className={`w-1 rounded-full transition-colors ${i < filled ? tone : 'bg-neutral-800'}`}
          style={{ height: `${h * 2}px` }}
        />
      ))}
    </div>
  );
}

function flagUrl(countryCode: string): string | null {
  const cc = countryCode.toLowerCase();
  // flagcdn unterstützt eu, gb, us, de — wenn unbekannt, fallback emoji
  if (['us', 'de', 'eu', 'gb', 'fr', 'it', 'es', 'jp', 'cn'].includes(cc)) {
    return `https://flagcdn.com/w40/${cc}.png`;
  }
  return null;
}

function flagEmoji(countryCode: string): string {
  switch (countryCode.toUpperCase()) {
    case 'US':
      return '🇺🇸';
    case 'DE':
      return '🇩🇪';
    case 'EU':
      return '🇪🇺';
    case 'GB':
      return '🇬🇧';
    default:
      return '🌐';
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const cardVariants = {
  hidden: { y: 16, opacity: 0, filter: 'blur(4px)' },
  visible: {
    y: 0,
    opacity: 1,
    filter: 'blur(0px)',
    transition: { type: 'spring' as const, stiffness: 240, damping: 22 },
  },
};

export function EconomicCalendar({ title, isToday, events, onAskEve }: EconomicCalendarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft;
      const max = el.scrollWidth - el.clientWidth - 1;
      setCanLeft(left > 0);
      setCanRight(left < max);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [events]);

  function scroll(direction: 'left' | 'right') {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }

  if (events.length === 0) {
    return (
      <section className="mb-4">
        <Header title={title} isToday={isToday} canLeft={false} canRight={false} onScroll={scroll} />
        <div className="glass rounded-2xl border border-dashed border-white/10 px-4 py-6 text-center text-[12px] text-neutral-500">
          Keine Ereignisse an diesem Tag.
        </div>
      </section>
    );
  }

  return (
    <section className="mb-5">
      <Header
        title={title}
        isToday={isToday}
        canLeft={canLeft}
        canRight={canRight}
        onScroll={scroll}
        eventsCount={events.length}
      />
      <div ref={scrollRef} className="scroll-hidden overflow-x-auto pb-1">
        <motion.div
          className="flex flex-nowrap gap-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {events.map((event, i) => (
            <EventCard key={`${event.eventName}-${event.timeBerlin}-${i}`} event={event} onAskEve={onAskEve} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Header({
  title,
  isToday,
  canLeft,
  canRight,
  onScroll,
  eventsCount,
}: {
  title: string;
  isToday?: boolean;
  canLeft: boolean;
  canRight: boolean;
  onScroll: (d: 'left' | 'right') => void;
  eventsCount?: number;
}) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h3 className={`text-[13px] font-bold uppercase tracking-wider ${isToday ? 'text-amber-300' : 'text-neutral-300'}`}>
          {title}
        </h3>
        {isToday ? (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-300">
            Heute
          </span>
        ) : null}
        {eventsCount !== undefined ? (
          <span className="text-[10px] text-neutral-600">
            · {eventsCount} {eventsCount === 1 ? 'Ereignis' : 'Ereignisse'}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        <AnimatePresence>
          {canLeft ? (
            <motion.button
              key="left"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => onScroll('left')}
              aria-label="Nach links scrollen"
              className="grid size-7 place-items-center rounded-full border border-white/10 bg-neutral-900/60 text-neutral-300 hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-300"
            >
              <ChevronLeft className="size-3.5" />
            </motion.button>
          ) : null}
        </AnimatePresence>
        <AnimatePresence>
          {canRight ? (
            <motion.button
              key="right"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => onScroll('right')}
              aria-label="Nach rechts scrollen"
              className="grid size-7 place-items-center rounded-full border border-white/10 bg-neutral-900/60 text-neutral-300 hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-300"
            >
              <ChevronRight className="size-3.5" />
            </motion.button>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}

function EventCard({
  event,
  onAskEve,
}: {
  event: EconomicEvent;
  onAskEve?: (e: EconomicEvent) => void;
}) {
  const url = flagUrl(event.countryCode);
  const emoji = flagEmoji(event.countryCode);

  const timeTone =
    event.impact === 'high'
      ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
      : event.impact === 'medium'
        ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
        : 'border-white/10 bg-white/[0.03] text-neutral-300';

  return (
    <motion.article
      variants={cardVariants}
      whileHover={{ y: -2 }}
      className="glass-strong group flex w-72 shrink-0 flex-col rounded-2xl border border-white/5 p-4 transition-colors hover:border-amber-500/30"
    >
      {/* Top: Lokal-Zeit (Berlin) oben, ET drunter, Volatility rechts */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex flex-col gap-0.5">
          <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold tabular tracking-wide ${timeTone}`}>
            {event.timeBerlin}
          </span>
          <span className="px-2 text-[10px] tabular text-neutral-500">
            {event.timeEt} ET
          </span>
        </div>
        <VolatilityBars impact={event.impact} />
      </div>

      {/* Middle: Flag + Indicator */}
      <div className="mb-2 flex items-center gap-3">
        <div className="grid size-8 shrink-0 place-items-center rounded-full bg-neutral-900/80 ring-1 ring-white/10">
          {url ? (
            <img
              src={url}
              alt={`Flagge ${event.countryCode}`}
              className="size-7 rounded-full object-cover"
              loading="lazy"
              onError={(e) => {
                // Falls Image-Load fehlschlägt (z.B. EU manchmal), fallback emoji
                const target = e.currentTarget;
                target.style.display = 'none';
                const sib = target.nextElementSibling as HTMLElement | null;
                if (sib) sib.style.display = 'block';
              }}
            />
          ) : null}
          <span className={url ? 'hidden text-base' : 'text-base'}>{emoji}</span>
        </div>
        <h4 className="text-[13px] font-semibold leading-tight text-neutral-100" title={event.eventName}>
          {event.eventName}
        </h4>
      </div>

      {/* Erklärung — was misst dieser Indikator + Markt-Relevanz (Doc 2026-05-17) */}
      <EventExplanation name={event.eventName} />

      {/* Footer: 3-Col Aktuell · Prognose · Vorher */}
      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-white/5 pt-3 text-center text-[11px]">
        <Stat label="Aktuell" value={event.actual} tone="emerald" />
        <Stat label="Prognose" value={event.forecast} tone="amber" />
        <Stat label="Vorher" value={event.prior} tone="neutral" />
      </div>

      <div className="mt-3 flex gap-2">
        {onAskEve ? (
          <button
            type="button"
            onClick={() => onAskEve(event)}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-500/20"
          >
            <Sparkles className="size-3" />
            Frag EVE
          </button>
        ) : null}
        <AddToCalendarMenu event={event} />
      </div>
    </motion.article>
  );
}

function AddToCalendarMenu({ event }: { event: EconomicEvent }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const calEvent: SingleCalEvent = {
    date: event.date,
    timeBerlin: event.timeBerlin,
    timeEt: event.timeEt,
    indicator: event.eventName,
    country: event.countryCode,
    importance: event.impact,
    forecast: event.forecast,
    prior: event.prior,
    actual: event.actual,
  };
  const googleUrl = buildGoogleCalendarUrl(calEvent);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-label="In Kalender speichern"
        className="inline-flex items-center justify-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-[11px] font-medium text-neutral-300 transition-colors hover:border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-300"
      >
        <CalendarPlus className="size-3" />
        Kalender
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 z-20 mt-1 w-52 rounded-xl border border-white/10 bg-neutral-900/95 p-1.5 shadow-xl backdrop-blur"
          >
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[12px] text-neutral-200 hover:bg-amber-500/10 hover:text-amber-300"
            >
              <span className="text-base leading-none">🟢</span>
              Google Calendar
            </a>
            <button
              type="button"
              onClick={() => {
                downloadSingleEventIcal(calEvent);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-neutral-200 hover:bg-amber-500/10 hover:text-amber-300"
            >
              <span className="text-base leading-none">📅</span>
              Apple / Outlook (.ics)
            </button>
            <p className="mt-1 border-t border-white/5 px-2 py-1 text-[10px] text-neutral-500">
              30 min Default · Reminder bei Hoch-Impact
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function EventExplanation({ name }: { name: string }) {
  const text = getIndicatorExplanation(name);
  if (!text) {
    return (
      <p className="text-[11px] italic text-neutral-600">
        Kein Glossar-Eintrag — Frag EVE für Kontext.
      </p>
    );
  }
  return (
    <p className="text-[11px] leading-snug text-neutral-400">{text}</p>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | null;
  tone: 'emerald' | 'amber' | 'neutral';
}) {
  const valueTone =
    value === null
      ? 'text-neutral-600'
      : tone === 'emerald'
        ? 'text-emerald-300'
        : tone === 'amber'
          ? 'text-amber-300'
          : 'text-neutral-200';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`mt-0.5 font-medium tabular ${valueTone}`}>{value ?? '—'}</div>
    </div>
  );
}
